// src/home/HomeScreenSettings.jsx
import { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import "./HomeScreenSettings.css";

const createSection = (override = {}) => ({
  id: override.id || `section-${Date.now()}`,
  title: override.title || "New Section",
  description: override.description || "Describe the section purpose here.",
  items: Array.isArray(override.items) ? override.items : [],
  link: override.link || "/",
  enabled: override.enabled !== false,
});

const getItemUrl = (item) => {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return "";
  return item.url || item.imageUrl || item.imageUrls?.[0] || item.media_url || "";
};

const getItemId = (item) => {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return "";
  return item._id || item.id || item.media_id || getItemUrl(item);
};

const normalizeSectionItem = (item) => {
  if (typeof item === "string") {
    return { url: item };
  }
  if (item && typeof item === "object") {
    const normalizedItem = { ...item };
    const url = getItemUrl(item);
    if (url) normalizedItem.url = url;
    return normalizedItem;
  }
  return { url: "" };
};

const isSameSectionItem = (left, right) => {
  const leftId = getItemId(left);
  const rightId = getItemId(right);
  return Boolean(leftId && rightId && leftId === rightId);
};

const normalizeSections = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((section, index) => {
    if (!section || typeof section !== "object") {
      return createSection({ id: `section-${Date.now() + index}` });
    }
    const normalizedItems = Array.isArray(section.items)
      ? section.items
          .map(normalizeSectionItem)
          .filter((item) => getItemUrl(item))
      : [];
    return {
      ...createSection(section),
      id: section.id || `section-${Date.now() + index}`,
      title: section.title || "Section",
      description: section.description || "Describe the section purpose here.",
      items: normalizedItems,
      link: section.link || "/",
      enabled: section.enabled !== false,
    };
  });
};

const isImageValue = (value) => Boolean(value && (/\.(jpg|jpeg|png|gif|webp|bmp|svg|jfif|ico)$/i.test(value) || value.startsWith("data:image/")));
const isVideoValue = (value) => Boolean(value && (/\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(value) || value.startsWith("data:video/")));
const getMediaType = (value) => {
  if (isVideoValue(value)) return "video";
  if (isImageValue(value)) return "image";
  return "unknown";
};

const defaultSections = [
  {
    id: "section-1784481160693",
    title: "Slider",
    description: "Describe the section purpose here.",
    items: [
      "https://ik.imagekit.io/i3giodzh1/dharmiq_app_images/BalKrishnaWithFlute_1ekcgaW4r.jpg",
      "https://ik.imagekit.io/i3giodzh1/dharmiq_app_images/KrishnaBehindWallSideFace_4Bfd-NObZ.jpg",
    ],
    link: "/",
    enabled: true,
  },
  {
    id: "section-1784481160694",
    title: "Video",
    description: "Describe the section purpose here.",
    items: [
      "https://ik.imagekit.io/i3giodzh1/dharmiq_app_images/ganpati_video.mp4",
    ],
    link: "/",
    enabled: true,
  },
];

export default function HomeScreenSettings({ darkMode }) {
  const [sections, setSections] = useState(() => normalizeSections(defaultSections));
  const [statusMessage, setStatusMessage] = useState("Ready to save");
  const [mediaOptions, setMediaOptions] = useState([]);
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);
  const [mediaPage, setMediaPage] = useState({});
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [loadingSections, setLoadingSections] = useState(true);
  const pageSize = 24;

  // Load settings from API
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoadingSections(true);
        const stored = window.localStorage.getItem("dharmiq-home-screen-settings");
        if (stored) {
          const parsedStored = JSON.parse(stored);
          const normalizedStored = normalizeSections(parsedStored);
          if (normalizedStored.length) {
            setSections(normalizedStored);
          }
        }

        const base_url = process.env.REACT_APP_ENV === "local"
          ? process.env.REACT_APP_BACKEND_API_LOCAL
          : process.env.REACT_APP_BACKEND_API;

        const res = await fetch(`${base_url}/api/home-screen/home-screen-settings`, {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
          },
        });

        if (!res.ok) throw new Error(`Load settings failed ${res.status}`);

        const json = await res.json();
        if (json?.data?.sections) {
          const normalizedSections = normalizeSections(json.data.sections);
          setSections(normalizedSections);
          window.localStorage.setItem("dharmiq-home-screen-settings", JSON.stringify(normalizedSections));
        }
        setHasLoadedSettings(true);
      } catch (err) {
        console.error("Failed to load home screen settings", err);
        if (!hasLoadedSettings) {
          setSections(normalizeSections(defaultSections));
        }
        setHasLoadedSettings(true);
      } finally {
        setLoadingSections(false);
      }
    };

    loadSettings();
  }, []);

  // Load media options from API
  useEffect(() => {
    const loadMediaOptions = async () => {
      try {
        setLoadingMedia(true);
        const base_url = process.env.REACT_APP_ENV === "local"
          ? process.env.REACT_APP_BACKEND_API_LOCAL
          : process.env.REACT_APP_BACKEND_API;


            const reqBody = {
                    type:"image"
                }
            const res = await fetch(`${base_url}/api/home/admin-media-data`, {
               method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify(reqBody),
            });



        // const res = await fetch(`${base_url}/api/home/admin-media-data`, {
        //   headers: {
        //     Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
        //   },
        // });

        if (!res.ok) throw new Error(`Media fetch failed ${res.status}`);

        const json = await res.json();
        let list = [];
        if (json?.data) {
          list = Array.isArray(json.data) ? json.data : [];
        } else if (Array.isArray(json)) {
          list = json;
        }
        setMediaOptions(list);
      } catch (err) {
        console.error("Failed to load media options", err);
        setMediaOptions([]);
      } finally {
        setLoadingMedia(false);
      }
    };

    loadMediaOptions();
  }, []);

  // Save sections to localStorage
  useEffect(() => {
    try {
      window.localStorage.setItem("dharmiq-home-screen-settings", JSON.stringify(sections));
    } catch (err) {
      console.error("Failed to save home screen settings", err);
    }
  }, [sections]);

  useEffect(() => {
    setMediaPage({});
  }, [searchTerm, showSelectedOnly]);

  const updateSection = (sectionId, field, value) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    );
  };

  const moveSection = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setSections((prev) => {
      const sourceIndex = prev.findIndex((section) => section.id === sourceId);
      const targetIndex = prev.findIndex((section) => section.id === targetId);
      if (sourceIndex === -1 || targetIndex === -1) return prev;
      const nextSections = [...prev];
      const [movedSection] = nextSections.splice(sourceIndex, 1);
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      nextSections.splice(adjustedTargetIndex, 0, movedSection);
      return nextSections;
    });
  };

  const toggleItemSelection = (sectionId, item) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        const normalizedItem = normalizeSectionItem(item);
        const isSelected = section.items.some((existingItem) => isSameSectionItem(existingItem, normalizedItem));
        const nextItems = isSelected
          ? section.items.filter((existingItem) => !isSameSectionItem(existingItem, normalizedItem))
          : [...section.items, normalizedItem];
        return { ...section, items: nextItems };
      })
    );
  };

  const addSection = () => {
    const newSection = createSection();
    setSections((prev) => [...prev, newSection]);
    setStatusMessage("New section added");
  };

  const removeSection = (sectionId) => {
    setSections((prev) => prev.filter((section) => section.id !== sectionId));
    setStatusMessage("Section removed");
  };

  const saveSettings = async () => {
    try {
      const base_url = process.env.REACT_APP_ENV === "local"
        ? process.env.REACT_APP_BACKEND_API_LOCAL
        : process.env.REACT_APP_BACKEND_API;

      const res = await fetch(`${base_url}/api/home-screen/home-screen-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
        },
        body: JSON.stringify({ sections, updatedBy: "admin" }),
      });

      if (!res.ok) throw new Error(`Save settings failed ${res.status}`);

      window.localStorage.setItem("dharmiq-home-screen-settings", JSON.stringify(normalizeSections(sections)));
      setStatusMessage("Settings saved successfully");
      setTimeout(() => setStatusMessage("Ready to save"), 1800);
    } catch (err) {
      console.error("Failed to save home screen settings", err);
      setStatusMessage("Unable to save changes");
    }
  };

  const getVisibleMediaOptions = (section, mediaType = "image") => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return mediaOptions.filter((media) => {
      const value = getItemUrl(media);
      let detectedType = getMediaType(value);
      if (detectedType === "unknown") {
        if (media.type === "video") detectedType = "video";
        else if (media.type === "image") detectedType = "image";
        else if (media.fileType === "video") detectedType = "video";
        else if (media.fileType === "image") detectedType = "image";
        else if (value) {
          const urlLower = value.toLowerCase();
          if (urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.webm') || urlLower.includes('.avi')) {
            detectedType = "video";
          } else {
            detectedType = "image";
          }
        }
      }
      if (detectedType !== mediaType) return false;
      const searchableText = [
        media.name || "",
        media.title || "",
        media.filename || "",
        value || "",
        ...(Array.isArray(media.tags) ? media.tags : [])
      ].filter(Boolean).join(" ").toLowerCase();
      const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);
      const isSelected = section.items.some((existingItem) => isSameSectionItem(existingItem, media));
      return matchesSearch && (!showSelectedOnly || isSelected);
    });
  };

  const getSectionPage = (sectionId, mediaType) => mediaPage[sectionId]?.[mediaType] || 1;

  const setSectionPage = (sectionId, mediaType, nextPage) => {
    setMediaPage((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [mediaType]: Math.max(1, nextPage),
      },
    }));
  };

  const previewMediaType = previewImage ? getMediaType(previewImage) : null;

  // If still loading, show loading state
  if (loadingSections) {
    return (
      <div className={`home-screen-container ${darkMode ? 'dark' : ''}`}>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading sections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`home-screen-container ${darkMode ? 'dark' : ''}`}>
      {/* Preview Modal */}
      {previewImage && (
        <div className="preview-overlay" onClick={() => setPreviewImage(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {previewMediaType === "video" ? (
              <video
                src={previewImage}
                controls
                autoPlay
                className="preview-video"
              />
            ) : (
              <img
                src={previewImage}
                alt="Preview"
                className="preview-image"
              />
            )}
          </div>
        </div>
      )}

      {/* Page Content */}
      <div className="home-screen-content">
        <PageHeader 
          title="Home Screen Builder" 
          buttonText="Save Changes" 
          onAdd={saveSettings} 
        />

        {/* Status Message */}
        <div className="status-message">
          <strong>{statusMessage}</strong>
          <div className="status-subtext">
            Configure sections like slider items, "For You", and top trending content from here.
          </div>
        </div>

        {/* Toolbar */}
        <div className="home-screen-toolbar">
          <div className="toolbar-left">
            <div className="search-wrapper">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search media..."
                className="search-input-modern"
              />
            </div>
            <button 
              className={`filter-btn ${showSelectedOnly ? 'active' : ''}`}
              onClick={() => setShowSelectedOnly((prev) => !prev)}
            >
              {showSelectedOnly ? "Showing selected" : "Show selected only"}
            </button>
            {searchTerm && (
              <button className="clear-btn" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            )}
            <span className="media-count">
              {mediaOptions.length} total media items
            </span>
          </div>
          <button className="add-section-btn" onClick={addSection}>
            + Add New Section
          </button>
        </div>

        {/* Sections */}
        <div className="sections-container">
          {sections.length === 0 ? (
            <div className="no-sections">
              <p>No sections found. Click "Add New Section" to get started.</p>
            </div>
          ) : (
            sections.map((section) => {
              const isSectionVisible = section.enabled !== false;
              return (
                <div
                  key={section.id}
                  draggable
                  onDragStart={() => setDraggedSectionId(section.id)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverSectionId(section.id);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    moveSection(draggedSectionId, section.id);
                    setDraggedSectionId(null);
                    setDragOverSectionId(null);
                  }}
                  onDragEnd={() => {
                    setDraggedSectionId(null);
                    setDragOverSectionId(null);
                  }}
                  className={`section-card ${!isSectionVisible ? 'hidden' : ''} ${dragOverSectionId === section.id ? 'drag-over' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => removeSection(section.id)}
                    className="remove-section-btn"
                  >
                    ×
                  </button>

                  <div className="section-content">
                    <div className="section-header-area">
                      <div className="section-title-area">
                        <span className="section-badge">
                          {section.id === "section-1784481160693" ? "Slider" : 
                           section.id === "section-1784481160694" ? "Video" : 
                           section.id === "for-you" ? "For You" : 
                           section.id === "top-trending" ? "Trending" : "Section"}
                        </span>
                      </div>
                      
                      {!section.isCollapsed ? (
                        <>
                          <input
                            value={section.title}
                            onChange={(e) => updateSection(section.id, "title", e.target.value)}
                            className="section-input"
                            placeholder="Section title"
                          />
                          <textarea
                            value={section.description}
                            onChange={(e) => updateSection(section.id, "description", e.target.value)}
                            className="section-textarea"
                            placeholder="Section description"
                          />
                          <input
                            value={section.link || "/"}
                            onChange={(e) => updateSection(section.id, "link", e.target.value)}
                            className="section-input"
                            placeholder="Destination link"
                          />
                        </>
                      ) : (
                        <div className="collapsed-title">
                          {section.title || "Untitled section"}
                        </div>
                      )}

                      <div className="section-actions">
                        <span className="drag-handle">⋮⋮ Drag to reorder</span>
                        <label className="visibility-label">
                          <input
                            type="checkbox"
                            checked={section.enabled !== false}
                            onChange={(e) => updateSection(section.id, "enabled", e.target.checked)}
                          />
                          Visible
                        </label>
                        <button
                          className="action-btn"
                          onClick={() => updateSection(section.id, "enabled", !isSectionVisible)}
                        >
                          {isSectionVisible ? "Hide card" : "Show card"}
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => updateSection(section.id, "isCollapsed", !section.isCollapsed)}
                        >
                          {section.isCollapsed ? "Expand section" : "Collapse section"}
                        </button>
                      </div>
                    </div>

                    {!section.isCollapsed && (
                      <div className="section-body">
                        <div className="media-select-header">
                          <label className="media-label">Select uploaded media</label>
                          <span className="media-stats">
                            {loadingMedia ? "Loading media..." : 
                             `${getVisibleMediaOptions(section, "image").length} images • ${getVisibleMediaOptions(section, "video").length} videos`}
                          </span>
                        </div>

                        <div className="media-grid-container">
                          {[
                            { type: "image", label: "Images" },
                            { type: "video", label: "Videos" },
                          ].map((mediaGroup) => {
                            const visibleMediaOptions = getVisibleMediaOptions(section, mediaGroup.type);
                            const sectionPage = getSectionPage(section.id, mediaGroup.type);
                            const sectionTotalPages = Math.max(1, Math.ceil(visibleMediaOptions.length / pageSize));
                            const pagedMediaOptions = visibleMediaOptions.slice((sectionPage - 1) * pageSize, sectionPage * pageSize);

                            if (visibleMediaOptions.length === 0 && !loadingMedia) {
                              return null;
                            }

                            return (
                              <div key={mediaGroup.type} className="media-group">
                                <div className="media-group-header">
                                  <strong>{mediaGroup.label}</strong>
                                  <span>{visibleMediaOptions.length} {mediaGroup.type}s</span>
                                </div>

                                {loadingMedia ? (
                                  <div className="loading-media">Loading media...</div>
                                ) : pagedMediaOptions.length === 0 ? (
                                  <div className="no-media">No {mediaGroup.type}s available</div>
                                ) : (
                                  <div className="media-grid">
                                    {pagedMediaOptions.map((media) => {
                                      const value = getItemUrl(media);
                                      const isSelected = section.items.some((existingItem) => isSameSectionItem(existingItem, media));
                                      return (
                                        <button
                                          key={getItemId(media) || value}
                                          type="button"
                                          onClick={() => toggleItemSelection(section.id, media)}
                                          onDoubleClick={() => setPreviewImage(value)}
                                          className={`media-item ${isSelected ? 'selected' : ''}`}
                                        >
                                          {isSelected && <div className="selected-badge">✓</div>}
                                          {mediaGroup.type === "video" ? (
                                            <video
                                              src={value}
                                              muted
                                              preload="metadata"
                                              className="media-thumbnail"
                                            />
                                          ) : (
                                            <img
                                              src={value}
                                              alt={media.name || "Uploaded media"}
                                              className="media-thumbnail"
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                                e.target.parentElement.style.background = "rgba(239, 68, 68, 0.1)";
                                              }}
                                            />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                {visibleMediaOptions.length > pageSize && (
                                  <div className="pagination-controls">
                                    <button
                                      className="page-btn"
                                      disabled={sectionPage === 1}
                                      onClick={() => setSectionPage(section.id, mediaGroup.type, sectionPage - 1)}
                                    >
                                      Prev
                                    </button>
                                    <button
                                      className="page-btn"
                                      disabled={sectionPage >= sectionTotalPages}
                                      onClick={() => setSectionPage(section.id, mediaGroup.type, sectionPage + 1)}
                                    >
                                      Next
                                    </button>
                                    <span className="page-info">
                                      Page {sectionPage} of {sectionTotalPages}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {section.items.length > 0 && (
                          <div className="selected-items">
                            <label className="selected-label">Selected Items ({section.items.length})</label>
                            <div className="selected-grid">
                              {section.items.map((item) => {
                                const itemUrl = getItemUrl(item);
                                const itemType = getMediaType(itemUrl);
                                return (
                                  <div
                                    key={getItemId(item) || itemUrl}
                                    onDoubleClick={() => setPreviewImage(itemUrl)}
                                    className="selected-item"
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleItemSelection(section.id, item);
                                      }}
                                      className="remove-selected-btn"
                                    >
                                      ×
                                    </button>
                                    {itemType === "video" ? (
                                      <video
                                        src={itemUrl}
                                        muted
                                        preload="metadata"
                                        className="selected-thumbnail"
                                      />
                                    ) : (
                                      <img
                                        src={itemUrl}
                                        alt="Selected media"
                                        className="selected-thumbnail"
                                        onError={(e) => {
                                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='72' height='72'%3E%3Crect width='72' height='72' fill='%23020617'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23ef4444' font-size='10'%3EError%3C/text%3E%3C/svg%3E";
                                        }}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}